// src/lib/http.js
import axios from 'axios';

// ---- Normalize base URL (remove trailing slashes)
const RAW_BASE = process.env.VUE_APP_API_BASE_URL || '';
const BASE = RAW_BASE.replace(/\/+$/, ''); // e.g. "http://localhost:5000/"" -> "http://localhost:5000"

const http = axios.create({
  baseURL: BASE, // normalized
  timeout: 15000,
  withCredentials: false,
  headers: {
    Accept: 'application/json',
  },
});

/** ========= Helpers ========= **/
function getRefreshToken() {
  return localStorage.getItem('refresh_token');
}
function setAccessToken(token) {
  // Keep both keys in sync with your current convention
  localStorage.setItem('auth_token', token);    // used by axios request interceptor
  localStorage.setItem('access_token', token);  // also kept for reference
}
function clearAuth() {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_email');
  localStorage.removeItem('user_handle');
  // NOTE: do NOT clear profile_details cache here; only on explicit logout later.
}

/** ========= Request: attach Bearer ========= **/
http.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

/** ========= Single-flight refresh gate ========= **/
let isRefreshing = false;
let pendingQueue = []; // queued resolvers while a refresh is in flight

function processQueue(error, token = null) {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve(token);
  });
  pendingQueue = [];
}

/** ========= Response: auto-refresh on 401 (retry once) ========= **/
http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error?.config;

    // Network/unknown error -> just bubble up
    if (!error?.response) return Promise.reject(error);

    const status = error.response.status;

    // Only handle 401 if this request hasn't been retried yet
    if (status === 401 && original && !original._retry) {
      original._retry = true;

      // If a refresh is already in progress, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({
            resolve: (newToken) => {
              if (newToken) {
                original.headers = original.headers || {};
                original.headers.Authorization = `Bearer ${newToken}`;
              }
              resolve(http(original));
            },
            reject,
          });
        });
      }

      // Start refresh
      isRefreshing = true;

      return new Promise(async (resolve, reject) => {
        try {
          const rt = getRefreshToken();
          if (!rt) throw new Error('Missing refresh token');

          // Use normalized BASE to avoid "//auth/refresh"
          const refreshUrl = `${BASE}/auth/refresh`;

          // Call refresh using a plain axios instance to avoid interceptor loops
          const resp = await axios.post(
            refreshUrl,
            { refreshToken: rt },
            { headers: { Accept: 'application/json' }, timeout: 15000 }
          );

          const newAccess = resp?.data?.tokens?.accessToken;
          if (!newAccess) throw new Error('No access token in refresh response');

          // Persist and apply new token
          setAccessToken(newAccess);

          // Update the original request Authorization header
          original.headers = original.headers || {};
          original.headers.Authorization = `Bearer ${newAccess}`;

          // Resolve queued requests
          processQueue(null, newAccess);

          // Retry the original request
          resolve(http(original));
        } catch (err) {
          processQueue(err, null);

          // Only clear auth for definitive auth failures
          const s = err?.response?.status;
          if (s === 400 || s === 401 || s === 403) {
            // refresh token invalid/expired → real logout case
            clearAuth();
          } else {
            // network/timeout/5xx: keep tokens; let caller decide UI/ retry later
            console.warn('Refresh failed (non-auth error); preserving tokens:', err?.message || err);
          }

          reject(err);
        } finally {
          isRefreshing = false;
        }
      });
    }

    // Non-401 or already retried: bubble up
    return Promise.reject(error);
  }
);

export default http;
